import React, { useState, useMemo, useEffect } from 'react';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import MiscUtils from 'services/MiscUtils';
import CalendarUtils from 'services/CalendarUtils';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import { DefaultCheckbox } from 'services/UIUtils';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import moment from 'moment-timezone';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import {
  calculatePrevent,
  calculateEgfrCkdEpi2021,
  getPreventRiskCategory,
  PREVENT_RANGES,
} from './calculatePrevent';

const DEFAULT_ARRAY = [];
const NAME = 'PREVENT'; // distinct from 'FRS' so the v:1 discriminators never collide

// Lab values can carry qualifiers (>1000, <5, "12.3 H", "Hemolyzed"). parseFloat
// silently truncates "12.3 H" to 12.3 — flag those so we can warn the practitioner
// instead of computing on a half-parsed value. Copied verbatim from FIB4CalculatorModal.
function parseLabValue(raw) {
  if (raw == null || raw === '') return { value: undefined, suspect: false };
  const trimmed = String(raw).trim();
  if (trimmed === '') return { value: undefined, suspect: false };
  const num = parseFloat(trimmed);
  if (!isFinite(num)) return { value: undefined, suspect: true };
  const suspect = !/^-?\d+(\.\d+)?$/.test(trimmed);
  return { value: trimmed, suspect };
}

// Strict shape match — free-form notes whose name happens to be 'PREVENT' won't
// parse and are ignored, so the modal can't clobber legacy free-text notes on save
// and the CPP rail won't render JSON for them. Mirrors FRS tryParseFRS.
function tryParsePREVENT(rawNotes) {
  if (typeof rawNotes !== 'string' || rawNotes[0] !== '{') return null;
  let p;
  try {
    p = JSON.parse(rawNotes);
  } catch {
    return null;
  }
  if (!p || p.v !== 1) return null;
  if (typeof p.result !== 'string') return null;
  if (p.gender !== 'Male' && p.gender !== 'Female') return null;
  const nums = ['age', 'totalCholesterol', 'hdl', 'systolicBP', 'eGFR', 'calculatedAt'];
  if (nums.some((k) => typeof p[k] !== 'number')) return null;
  const bools = ['bpMedication', 'diabetes', 'smoker', 'statin'];
  if (bools.some((k) => typeof p[k] !== 'boolean')) return null;
  if (p.source !== 'manual' && p.source !== 'auto-lab') return null;
  return p;
}

function decodeNotes(rawNotes) {
  const parsed = tryParsePREVENT(rawNotes);
  if (!parsed) return null;
  const when = CalendarUtils.format(parsed.calculatedAt, 'YYYY/MM/DD');
  const isAuto = parsed.source === 'auto-lab';
  const headline = `PREVENT ${parsed.result} 10-yr ASCVD${isAuto ? ' (auto)' : ''}`;
  const detailJsx = (
    <span>
      TC {parsed.totalCholesterol} / HDL {parsed.hdl} mmol/L
      <br />
      SBP {parsed.systolicBP} {'·'} eGFR {parsed.eGFR}
      {parsed.bpMedication ? ' (on BP med)' : ''}
      {parsed.smoker ? ', smoker' : ''}
      {parsed.diabetes ? ', DM' : ''}
      {parsed.statin ? ', statin' : ''}
    </span>
  );
  return { parsed, when, isAuto, headline, detailJsx };
}

export const PREVENTCalculatorUtils = {
  NAME,
  tryParsePREVENT,
  decodeNotes,
  decodeForDisplay(item) {
    const decoded = decodeNotes(item?.value?.notes);
    if (!decoded) return null;
    return {
      text: decoded.headline,
      notes: (
        <span>
          {decoded.detailJsx}
          <br />
          {decoded.isAuto ? 'Auto-updated' : 'Calculated'} {decoded.when}
        </span>
      ),
    };
  },
};

// AHA PREVENT 10-yr ASCVD calculator (base model). Staff-only — only ever mounted
// from the CPP, never the patient portal. Mirrors FRSCalculatorModal.
// https://www.mdcalc.com/calc/10491/predicting-risk-cardiovascular-disease-events-prevent
export const PREVENTCalculatorModal = ({ patient, onSubmit, initialPayload }) => {
  const dispatch = useAppDispatch();
  const patientHr = useAppSelector((state) => state.healthRecords.byId[patient.id]);
  const patientSummary = patientHr?.dataSummary || DEFAULT_ARRAY;

  // When invoked from CPP edit, initialPayload is the exact saved snapshot —
  // restore it verbatim and bypass fresh labs.
  const editing = !!initialPayload;

  // Prefill labs. Cholesterol stays mmol/L (NO conversion — unlike FRS). Creatinine
  // is µmol/L (Care&/Dynacare SI) and feeds the eGFR derivation.
  const { tcRaw, hdlRaw, sbpRaw, scrRaw, prefillSuspect } = useMemo(() => {
    const tc = parseLabValue(
      patientSummary.find(
        ({ metric, origin }) => metric.id === 'CHOLESTEROL' && !metric.type && origin === 'Lab',
      )?.last_value?.value1,
    );
    const hdlc = parseLabValue(
      patientSummary.find(
        ({ metric, origin }) => metric.id === 'HDL CHOLESTEROL' && origin === 'Lab',
      )?.last_value?.value1,
    );
    const sbp = parseLabValue(
      patientSummary.find(({ metric, origin }) => metric.id === 'bp' && origin === 'Practitioner')
        ?.last_value?.value1,
    );
    const scr = parseLabValue(
      patientSummary.find(({ metric, origin }) => metric.id === 'CREATININE' && origin === 'Lab')
        ?.last_value?.value1,
    );
    return {
      tcRaw: tc.value,
      hdlRaw: hdlc.value,
      sbpRaw: sbp.value,
      scrRaw: scr.value,
      prefillSuspect: tc.suspect || hdlc.suspect || sbp.suspect || scr.suspect,
    };
  }, [patientSummary]);

  // Strict-match prior PREVENT Private Note — reuse its id on save.
  const previousPREVENT = useMemo(() => {
    const notes = patientHr?.healthRecords?.notes ?? [];
    for (const n of notes) {
      if (n?.value?.name !== NAME) continue;
      if (n?.history?.[0]?.action === 'Removed') continue;
      const parsed = tryParsePREVENT(n?.value?.notes);
      if (parsed) return { id: n.id, ...parsed };
    }
    return null;
  }, [patientHr]);

  const [gender, setGender] = useState(
    initialPayload?.gender ?? previousPREVENT?.gender ?? patient.gender,
  );
  const [age, setAge] = useState(
    initialPayload?.age ?? CalendarUtils.calculateAgeNumber(patient.date_of_birth),
  );
  const [totalCholesterol, setTotalCholesterol] = useState(
    editing ? initialPayload.totalCholesterol : tcRaw ?? previousPREVENT?.totalCholesterol,
  );
  const [hdl, setHdl] = useState(editing ? initialPayload.hdl : hdlRaw ?? previousPREVENT?.hdl);
  const [systolicBP, setSystolicBP] = useState(
    editing ? initialPayload.systolicBP : sbpRaw ?? previousPREVENT?.systolicBP,
  );
  // Serum creatinine (µmol/L) — input that derives eGFR. Not part of the saved
  // payload; left blank when editing (the saved eGFR is restored directly instead).
  const [creatinine, setCreatinine] = useState(editing ? '' : scrRaw ?? '');
  // eGFR is REQUIRED. Initial: saved snapshot when editing, else derived from the
  // prefilled creatinine + initial age/gender, else blank for manual entry.
  const [eGFR, setEGFR] = useState(() => {
    if (editing) return initialPayload.eGFR;
    if (scrRaw == null || scrRaw === '') return '';
    const v = calculateEgfrCkdEpi2021({
      serumCreatinine: scrRaw,
      scrUnit: 'umol/L',
      ageYears: initialPayload?.age ?? CalendarUtils.calculateAgeNumber(patient.date_of_birth),
      gender: initialPayload?.gender ?? previousPREVENT?.gender ?? patient.gender,
    });
    return v == null ? '' : v.toFixed(0);
  });
  // Toggles: prior PREVENT only; cannot be inferred from labs.
  const [bpMedication, setBpMedication] = useState(
    initialPayload?.bpMedication ?? previousPREVENT?.bpMedication ?? false,
  );
  const [diabetes, setDiabetes] = useState(
    initialPayload?.diabetes ?? previousPREVENT?.diabetes ?? false,
  );
  const [smoker, setSmoker] = useState(initialPayload?.smoker ?? previousPREVENT?.smoker ?? false);
  const [statin, setStatin] = useState(initialPayload?.statin ?? previousPREVENT?.statin ?? false);
  const [saving, setSaving] = useState(false);

  // Keep eGFR in sync with creatinine/age/gender, but only re-derive when there's
  // a valid creatinine — so a manual eGFR (no creatinine on file) is never clobbered
  // to blank. Skipped in edit mode, where the saved eGFR is authoritative.
  useEffect(() => {
    if (editing) return;
    const scr = parseFloat(creatinine);
    if (!isFinite(scr) || scr <= 0) return;
    const v = calculateEgfrCkdEpi2021({
      serumCreatinine: creatinine,
      scrUnit: 'umol/L',
      ageYears: age,
      gender,
    });
    setEGFR(v == null ? '' : v.toFixed(0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatinine, age, gender]);

  const isLoadingMetrics = useIsLoading(api.HealthRecords.addHealthData);

  const result = calculatePrevent({
    gender,
    ageYears: age,
    totalCholesterol,
    hdl,
    cholesterolUnit: 'mmol/L',
    systolicBP,
    eGFR,
    bpMedication,
    diabetes,
    smoker,
    statin,
  });
  const resultText = result == null ? '' : `${result.toFixed(1)}%`;
  const category = result == null ? null : getPreventRiskCategory(result);

  // Range warnings — warn-only, mirrors FIB-4; never blocks save.
  const ageN = parseFloat(age);
  const sbpN = parseFloat(systolicBP);
  const egfrN = parseFloat(eGFR);
  const ageWarn =
    isFinite(ageN) && (ageN < PREVENT_RANGES.age.min || ageN > PREVENT_RANGES.age.max)
      ? 'PREVENT is validated for ages 30–79; interpret with caution.'
      : '';
  const sbpWarn =
    isFinite(sbpN) && (sbpN < PREVENT_RANGES.systolicBP.min || sbpN > PREVENT_RANGES.systolicBP.max)
      ? 'Systolic BP is outside the validated 90–180 mmHg range.'
      : '';
  const egfrWarn =
    isFinite(egfrN) && (egfrN < PREVENT_RANGES.eGFR.min || egfrN > PREVENT_RANGES.eGFR.max)
      ? 'eGFR is outside the validated 15–140 range — confirm creatinine units (µmol/L).'
      : '';

  function getResult() {
    return (
      `AHA PREVENT 10-yr ASCVD: ${resultText}${category ? ` (${category})` : ''}\n` +
      `Age: ${age}\nGender: ${gender}\nTotal Cholesterol: ${totalCholesterol} mmol/L\n` +
      `HDL: ${hdl} mmol/L\nSystolic BP: ${systolicBP} mmHg\neGFR: ${eGFR} mL/min/1.73m²\n` +
      `BP Medication: ${bpMedication ? 'Yes' : 'No'}\nDiabetes: ${diabetes ? 'Yes' : 'No'}\n` +
      `Smoker: ${smoker ? 'Yes' : 'No'}\nStatin: ${statin ? 'Yes' : 'No'}`
    );
  }

  async function copyToClipboard() {
    await navigator.clipboard.writeText(getResult());
  }

  async function save() {
    setSaving(true);
    try {
      if (result == null) {
        MiscUtils.showToastrError('PREVENT', 'Enter all required values (including eGFR).');
        return;
      }
      const calculatedAt = moment().unix();
      const payload = {
        v: 1,
        result: resultText,
        age: parseInt(age, 10),
        gender,
        totalCholesterol: parseFloat(totalCholesterol),
        hdl: parseFloat(hdl),
        cholesterolUnit: 'mmol/L',
        systolicBP: parseInt(systolicBP, 10),
        eGFR: parseFloat(eGFR),
        bpMedication: !!bpMedication,
        diabetes: !!diabetes,
        smoker: !!smoker,
        statin: !!statin,
        category: getPreventRiskCategory(result),
        calculatedAt,
        source: 'manual',
      };
      const noteData = { value: { name: NAME, notes: JSON.stringify(payload) } };
      // Reuse id only when the prior note parsed as a valid PREVENT payload.
      if (previousPREVENT?.id) noteData.id = previousPREVENT.id;

      // Sequential await — metric first. A metrics-side failure surfaces a toast
      // and early-returns before the note write, so partial-success is observable.
      // HealthDataValue.value is typed string, so persist the percent as a string.
      const metricResponse = await dispatch(
        api.HealthRecords.addHealthData({
          patientId: patient.id,
          healthData: {
            name: 'prevent',
            values: [{ date: calculatedAt, value: result.toFixed(1) }],
          },
        }),
        { errorTitle: 'PREVENT' },
      );
      if (!metricResponse) return;

      const noteResponse = await dispatch(
        api.HealthRecords.addOrEditHealthRecordPatientIssue({
          patientId: patient.id,
          field: 'notes',
          patientIssues: [noteData],
        }),
        { errorTitle: 'PREVENT' },
      );
      if (!noteResponse) return;

      MiscUtils.showToastrInfo({
        title: 'PREVENT',
        message: 'Saved to metrics and Private Notes.',
      });
    } finally {
      setSaving(false);
    }
  }

  const warnStyle = { marginTop: '6px', fontSize: '12px', color: '#b34700' };

  return (
    // Wider than the default 400px .modal: PREVENT has more fields and longer,
    // unit-bearing labels (e.g. "Total Cholesterol (mmol/L)") that wrap and
    // overlap at 400px, and the long title collides with the drag/pin icons.
    // maxWidth keeps it from overflowing a narrow pinned split panel.
    <div className="modal" style={{ width: '450px', maxWidth: '100%' }}>
      <div className="title">AHA PREVENT Risk Calculator (10-yr ASCVD)</div>
      <div className="padded-box">
        <div className="row">
          <DefaultTextField
            name="age"
            label="Age"
            value={age}
            onChange={(event) => setAge(event.target.value)}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultSelectField
            label="Gender"
            name="gender"
            value={gender}
            onChange={(event) => setGender(event.target.value)}
            inputs={['Male', 'Female']}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="total_cholesterol"
            label="Total Cholesterol (mmol/L)"
            autoFocus
            value={totalCholesterol}
            onChange={(event) => setTotalCholesterol(event.target.value)}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="hdl_cholesterol"
            label="HDL Cholesterol (mmol/L)"
            value={hdl}
            onChange={(event) => setHdl(event.target.value)}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="systolic_bp"
            label="Systolic BP (mmHg)"
            value={systolicBP}
            onChange={(event) => setSystolicBP(event.target.value)}
            style={{ width: 'calc(100% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="creatinine"
            label="Serum Creatinine (µmol/L)"
            value={creatinine}
            onChange={(event) => setCreatinine(event.target.value)}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="egfr"
            label="eGFR (mL/min/1.73m²)"
            value={eGFR}
            onChange={(event) => setEGFR(event.target.value)}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
        </div>
        <div className="row" style={{ marginTop: '10px' }}>
          <DefaultCheckbox
            name="bp_medication"
            value={bpMedication}
            onChange={() => setBpMedication(!bpMedication)}
            text="On blood pressure medication"
          />
        </div>
        <div className="row" style={{ marginTop: '5px' }}>
          <DefaultCheckbox
            name="diabetes"
            value={diabetes}
            onChange={() => setDiabetes(!diabetes)}
            text="Diabetes present"
          />
        </div>
        <div className="row" style={{ marginTop: '5px' }}>
          <DefaultCheckbox
            name="smoker"
            value={smoker}
            onChange={() => setSmoker(!smoker)}
            text="Current smoker"
          />
        </div>
        <div className="row" style={{ marginTop: '5px' }}>
          <DefaultCheckbox
            name="statin"
            value={statin}
            onChange={() => setStatin(!statin)}
            text="On statin"
          />
        </div>
        <div className="row" style={{ marginTop: '10px' }}>
          <DefaultTextField
            name="result"
            label="10-yr ASCVD Risk"
            value={resultText}
            disabled={true}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="category"
            label="Risk Category"
            value={category || ''}
            disabled={true}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
        </div>
        <div style={{ marginTop: '10px', fontSize: '12px', color: '#666', lineHeight: 1.4 }}>
          eGFR is required and derived from serum creatinine (CKD-EPI 2021, race-free). Creatinine
          is read in µmol/L. Cholesterol is entered in mmol/L.
        </div>
        {prefillSuspect && (
          <div style={warnStyle}>
            One or more prefilled lab values include qualifiers (e.g. "&gt;", "H"). Confirm the
            numeric values before saving.
          </div>
        )}
        {ageWarn && <div style={warnStyle}>{ageWarn}</div>}
        {sbpWarn && <div style={warnStyle}>{sbpWarn}</div>}
        {egfrWarn && <div style={warnStyle}>{egfrWarn}</div>}
        <DefaultButton
          name="Save"
          icon="floppy-disk"
          onClick={save}
          style={{ width: '100%', marginTop: '10px' }}
          disabled={result == null}
          loading={saving || isLoadingMetrics}
        />
        <DefaultButton
          name={onSubmit ? 'Submit' : 'Copy to Clipboard'}
          icon={onSubmit ? undefined : 'clipboard'}
          onClick={onSubmit ? () => onSubmit(getResult()) : copyToClipboard}
          style={{ width: '100%', marginTop: '10px' }}
          disabled={result == null}
          className="blue"
        />
      </div>
    </div>
  );
};

export default PREVENTCalculatorModal;
